
import React, { useState, useEffect, useCallback } from 'react';
import { IMAGES, CONTACT_INFO, Icons } from '../constants';
import { CONTENT } from '../content';
import { useMetadata } from '../lib/seo';

interface ContactProps {
  isStandalone?: boolean;
}

const Contact: React.FC<ContactProps> = ({ isStandalone = false }) => {
  useMetadata(CONTENT.contact.metadataTitle, CONTENT.contact.metadataDescription, CONTENT.metadata.keywords, undefined, isStandalone);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stressScore, setStressScore] = useState<string | null>(null);
  const [stressReport, setStressReport] = useState<string | null>(null);
  
  // CAPTCHA state
  const [captcha, setCaptcha] = useState({ a: 0, b: 0, result: 0 });
  const [userCaptcha, setUserCaptcha] = useState('');

  const generateCaptcha = useCallback(() => {
    const a = Math.floor(Math.random() * 10) + 1;
    const b = Math.floor(Math.random() * 10) + 1;
    setCaptcha({ a, b, result: a + b });
    setUserCaptcha('');
  }, []);

  useEffect(() => {
    generateCaptcha();
    
    // Initiële check voor stress-rapport
    const loadReport = () => {
      const score = localStorage.getItem('liza_stress_score');
      const fullReport = localStorage.getItem('liza_stress_full_report');
      if (score) setStressScore(score);
      if (fullReport) setStressReport(fullReport);
    };

    loadReport();

    // Luister naar updates van de StressCheck component
    const handleCompleted = (e: any) => {
      setStressScore(e.detail.score.toString());
      setStressReport(e.detail.report);
    };

    const handleReset = () => {
      setStressScore(null);
      setStressReport(null);
    };

    window.addEventListener('stressCheckCompleted' as any, handleCompleted);
    window.addEventListener('stressCheckReset' as any, handleReset);

    return () => {
      window.removeEventListener('stressCheckCompleted' as any, handleCompleted);
      window.removeEventListener('stressCheckReset' as any, handleReset);
    };
  }, [generateCaptcha]);

  const handleRemoveReport = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Directe reset zonder confirm voor betere werking
    setStressScore(null);
    setStressReport(null);
    localStorage.removeItem('liza_stress_score');
    localStorage.removeItem('liza_stress_full_report');
    
    // Informeer de rest van de app dat de check is gereset
    window.dispatchEvent(new CustomEvent('stressCheckReset'));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    // Valideer CAPTCHA
    if (parseInt(userCaptcha) !== captcha.result) {
      setError('De berekening is niet correct. Probeer het opnieuw.');
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Alleen meesturen als ze nog in de state staan
    if (stressScore) formData.set('stress_score', stressScore);
    if (stressReport) formData.set('volledig_stress_rapport', stressReport);

    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://formspree.io/f/maqbnrdq', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSubmitted(true);
        form.reset();
        localStorage.removeItem('liza_stress_score');
        localStorage.removeItem('liza_stress_full_report');
        setStressScore(null);
        setStressReport(null);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Er is iets misgegaan bij het verzenden. Probeer het later opnieuw.');
        generateCaptcha();
      }
    } catch (err) {
      setError('Er is een netwerkfout opgetreden. Controleer je verbinding en probeer het opnieuw.');
      generateCaptcha();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="py-24 bg-liza-bg relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-accent/5 speels-blob -mr-20 -mt-20 pointer-events-none"></div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="text-accent font-bold tracking-[0.4em] text-[10px] uppercase mb-4 block">{CONTENT.contact.badge}</span>
          <h1 className="text-5xl font-serif text-primary mb-6">{CONTENT.contact.title}</h1>
          <p className="text-gray-500 font-light text-lg">
            {CONTENT.contact.description}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 mb-12">
          <div className="bg-white p-10 md:p-14 rounded-[3rem] shadow-[0_30px_60px_-20px_rgba(172,107,107,0.15)] border border-white">
            {submitted ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-accent-light rounded-full flex items-center justify-center mx-auto mb-8 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-3xl font-serif text-primary mb-6 italic">{CONTENT.contact.successTitle}</h3>
                <p className="text-gray-500 font-light">{CONTENT.contact.successDesc}</p>
                <button 
                  onClick={() => { setSubmitted(false); generateCaptcha(); }}
                  className="mt-12 text-[10px] font-bold tracking-[0.3em] uppercase text-primary border-b border-primary pb-2 hover:text-accent hover:border-accent transition-colors"
                >
                  {CONTENT.contact.successButton}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
                {error && (
                  <div className="bg-red-50 text-red-600 p-4 rounded-2xl text-xs font-medium tracking-wide animate-pulse">
                    {error}
                  </div>
                )}
                
                {stressScore && (
                  <div className="bg-primary/5 p-6 rounded-3xl border border-primary/10 flex items-center justify-between gap-4 animate-in fade-in slide-in-from-top-2">
                    <div className="flex items-center gap-5">
                      <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                        <Icons.Check />
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest text-primary">
                          {CONTENT.contact.stressCheckAttached}
                        </p>
                        <p className="text-[10px] text-gray-500 font-light mt-0.5">
                          {CONTENT.contact.stressCheckCount.replace('{score}', stressScore)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveReport}
                      className="w-10 h-10 flex items-center justify-center rounded-full text-gray-400 hover:bg-red-100 hover:text-red-600 transition-all duration-300 border border-transparent hover:border-red-200"
                      title="Resultaten niet meesturen"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <input type="hidden" name="stress_score" value={stressScore} />
                    <textarea name="volledig_stress_rapport" value={stressReport || ''} className="hidden" readOnly />
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="naam" className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">
                      {CONTENT.contact.form.name} <span className="text-primary ml-1">*</span>
                    </label>
                    <input 
                      id="naam"
                      name="naam"
                      type="text" 
                      required 
                      className="w-full bg-liza-bg border border-transparent px-6 py-5 rounded-2xl text-base focus:outline-none focus:border-primary/30 focus:bg-liza-bg transition-all font-light" 
                      placeholder={CONTENT.contact.form.namePlaceholder} 
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">
                      {CONTENT.contact.form.email} <span className="text-primary ml-1">*</span>
                    </label>
                    <input 
                      id="email"
                      name="email"
                      type="email" 
                      required 
                      className="w-full bg-liza-bg border border-transparent px-6 py-5 rounded-2xl text-base focus:outline-none focus:border-primary/30 focus:bg-liza-bg transition-all font-light" 
                      placeholder={CONTENT.contact.form.emailPlaceholder} 
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label htmlFor="telefoon" className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">
                      {CONTENT.contact.form.phone} <span className="text-primary ml-1">*</span>
                    </label>
                    <input 
                      id="telefoon"
                      name="telefoon"
                      type="tel" 
                      required
                      className="w-full bg-liza-bg border border-transparent px-6 py-5 rounded-2xl text-base focus:outline-none focus:border-primary/30 focus:bg-liza-bg transition-all font-light" 
                      placeholder={CONTENT.contact.form.phonePlaceholder} 
                    />
                  </div>
                  <div>
                    <label htmlFor="onderwerp" className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">
                      {CONTENT.contact.form.subject} <span className="text-primary ml-1">*</span>
                    </label>
                    <select 
                      id="onderwerp"
                      name="onderwerp"
                      required
                      className="w-full bg-liza-bg border border-transparent px-6 py-5 rounded-2xl text-base focus:outline-none focus:border-primary/30 focus:bg-liza-bg transition-all font-light appearance-none"
                    >
                      {CONTENT.contact.form.subjectOptions.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="bericht" className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-3">
                    {CONTENT.contact.form.message} <span className="text-primary ml-1">*</span>
                  </label>
                  <textarea 
                    id="bericht"
                    name="bericht"
                    rows={4} 
                    required 
                    className="w-full bg-liza-bg border border-transparent px-6 py-5 rounded-2xl text-base focus:outline-none focus:border-primary/30 focus:bg-liza-bg transition-all font-light" 
                    placeholder={CONTENT.contact.form.messagePlaceholder}
                  ></textarea>
                </div>

                {/* CAPTCHA Sectie */}
                <div className="bg-liza-bg/40 p-6 rounded-3xl border border-dashed border-accent/40">
                  <label htmlFor="verification" className="block text-[10px] font-bold uppercase tracking-widest text-gray-600 mb-4">
                    {CONTENT.contact.form.verification} <span className="text-primary ml-1">*</span>
                  </label>
                  <div className="flex items-center gap-6">
                    <div className="bg-white px-6 py-4 rounded-xl shadow-sm border border-accent/20 font-serif italic text-primary text-lg min-w-[120px] text-center">
                      {captcha.a} + {captcha.b} = ?
                    </div>
                    <input 
                      id="verification"
                      type="number" 
                      value={userCaptcha}
                      onChange={(e) => setUserCaptcha(e.target.value)}
                      required 
                      className="flex-grow bg-white border border-transparent px-6 py-4 rounded-xl text-base focus:outline-none focus:border-primary/30 transition-all font-light shadow-sm" 
                      placeholder={CONTENT.contact.form.verificationPlaceholder} 
                    />
                  </div>
                </div>

                <button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={`w-full bg-primary text-white py-6 rounded-full font-bold uppercase tracking-[0.3em] text-[10px] hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'shadow-lg shadow-primary/20'}`}
                >
                  {isSubmitting ? CONTENT.contact.form.submitting : CONTENT.contact.form.submit}
                </button>
              </form>
            )}
          </div>
          <div className="flex flex-col">
            <div className="flex flex-col text-left">
              <h3 className="text-3xl font-serif text-primary mb-6 italic">{CONTENT.contact.practiceTitle}</h3>
              <p className="text-gray-500 leading-relaxed font-light text-base mb-8">
                {CONTENT.contact.practiceDesc}
              </p>
              
              <div className="flex items-start gap-4 mb-10">
                <div className="w-10 h-10 bg-accent-light rounded-full flex-shrink-0 flex items-center justify-center text-primary">
                  <Icons.Location />
                </div>
                <div>
                  <span className="font-bold text-primary uppercase text-[10px] tracking-widest block mb-1">{CONTENT.contact.addressLabel}</span>
                  <span className="text-gray-500 font-light">{CONTACT_INFO.address}<br />{CONTACT_INFO.zipCity}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 mb-12">
                <div className="sm:w-2/5 flex flex-col p-6 bg-white/50 rounded-3xl border border-white/50 hover:bg-white transition-colors duration-300">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">{CONTENT.contact.callLabel}</span>
                  <span className="text-gray-600 font-medium whitespace-nowrap">{CONTACT_INFO.phone}</span>
                </div>
                <div className="sm:w-3/5 flex flex-col p-6 bg-white/50 rounded-3xl border border-white/50 hover:bg-white transition-colors duration-300">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-primary mb-2">{CONTENT.contact.emailLabel}</span>
                  <span className="text-gray-600 font-medium break-words">{CONTACT_INFO.email}</span>
                </div>
              </div>
            </div>

            <div className="lg:mt-8 h-[350px] flex flex-col shrink-0">
              <div className="h-full bg-white rounded-[2.5rem] p-3 shadow-xl relative overflow-hidden group">
                  <img 
                    src={IMAGES.location} 
                    alt="Natuurlijke omgeving Oostvoorne" 
                    className="w-full h-full object-cover rounded-[1.8rem] grayscale group-hover:grayscale-0 transition-all duration-700" 
                    width="400"
                    height="326"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-primary/20 backdrop-blur-[2px]">
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT_INFO.fullAddress)}`} 
                        target="_blank" 
                        rel="noreferrer" 
                        className="bg-white px-10 py-4 text-[11px] font-bold tracking-[0.3em] uppercase shadow-2xl rounded-full text-primary hover:bg-primary hover:text-white transition-all duration-300"
                      >
                        {CONTENT.contact.routeButton}
                      </a>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
